<script setup>
import { computed } from 'vue';

const props = defineProps({
  conditions: {
    type: Array,
    default: () => [],
  },
  fields: {
    type: Array,
    default: () => [],
  },
  currentFieldId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update']);

const availableFields = computed(() => {
  return props.fields.filter(f => f.id !== props.currentFieldId);
});

const operators = [
  { value: 'equals', label: 'Igual a' },
  { value: 'not_equals', label: 'Diferente de' },
  { value: 'contains', label: 'Contem' },
  { value: 'not_contains', label: 'Nao contem' },
  { value: 'is_empty', label: 'Esta vazio' },
  { value: 'not_empty', label: 'Nao esta vazio' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
];

const actions = [
  { value: 'show', label: 'Mostrar' },
  { value: 'hide', label: 'Ocultar' },
  { value: 'require', label: 'Tornar obrigatório' },
  { value: 'unrequire', label: 'Tornar opcional' },
];

function addCondition() {
  const newConditions = [
    ...props.conditions,
    {
      field_id: '',
      operator: 'equals',
      value: '',
      action: 'show',
    },
  ];
  emit('update', newConditions);
}

function removeCondition(index) {
  const newConditions = [...props.conditions];
  newConditions.splice(index, 1);
  emit('update', newConditions);
}

function updateCondition(index, key, value) {
  const newConditions = [...props.conditions];
  newConditions[index] = { ...newConditions[index], [key]: value };
  emit('update', newConditions);
}

const needsValueInput = operator => {
  return !['is_empty', 'not_empty'].includes(operator);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div>
    <div class="flex items-center justify-between mb-3">
      <div>
        <h4 class="text-xs font-medium text-n-slate-11">Regras Condicionais</h4>
        <p class="text-[10px] text-n-slate-9 !mb-0 mt-0.5">
          Defina quando este campo deve aparecer.
        </p>
      </div>
      <button
        class="flex items-center gap-1 text-xs font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 transition-colors"
        @click="addCondition"
      >
        <span class="i-lucide-plus size-3" />
        Regra
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-if="!conditions.length"
      class="flex flex-col items-center py-8 text-center"
    >
      <div
        class="flex items-center justify-center size-10 rounded-xl bg-n-slate-3 mb-2"
      >
        <span class="i-lucide-git-branch size-5 text-n-slate-9" />
      </div>
      <p class="text-xs text-n-slate-9 !mb-0 max-w-[180px]">
        Este campo sera sempre visivel. Adicione regras para controlar quando
        ele aparece.
      </p>
    </div>

    <!-- Rules list -->
    <div v-else class="space-y-2">
      <div
        v-for="(condition, index) in conditions"
        :key="index"
        class="bg-n-slate-2/30 border border-n-weak rounded-lg p-3"
      >
        <!-- Rule header -->
        <div class="flex items-center justify-between mb-2">
          <span
            class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
          >
            {{ index === 0 ? 'Se' : 'E se' }}
          </span>
          <button
            class="flex items-center justify-center size-5 rounded text-n-slate-9 hover:text-red-500 transition-colors"
            @click="removeCondition(index)"
          >
            <span class="i-lucide-x size-3" />
          </button>
        </div>

        <!-- Field select -->
        <div class="mb-2">
          <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
            Campo
          </label>
          <select
            :value="condition.field"
            class="reset-base w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            @change="updateCondition(index, 'field', $event.target.value)"
          >
            <option value="">Selecione um campo</option>
            <option v-for="f in availableFields" :key="f.id" :value="f.name">
              {{ f.label }}
            </option>
          </select>
        </div>

        <!-- Operator select -->
        <div class="mb-2">
          <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
            Operador
          </label>
          <select
            :value="condition.operator"
            class="reset-base w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            @change="updateCondition(index, 'operator', $event.target.value)"
          >
            <option v-for="op in operators" :key="op.value" :value="op.value">
              {{ op.label }}
            </option>
          </select>
        </div>

        <!-- Value input -->
        <div v-if="needsValueInput(condition.operator)" class="mb-2">
          <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
            Valor
          </label>
          <input
            :value="condition.value"
            type="text"
            placeholder="Insira o valor..."
            class="reset-base w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            @input="updateCondition(index, 'value', $event.target.value)"
          />
        </div>

        <!-- Action select -->
        <div>
          <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
            Ação
          </label>
          <select
            :value="condition.action"
            class="reset-base w-full h-8 px-2 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
            @change="updateCondition(index, 'action', $event.target.value)"
          >
            <option v-for="act in actions" :key="act.value" :value="act.value">
              {{ act.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Logic connector info -->
      <p
        v-if="conditions.length > 1"
        class="text-[10px] text-n-slate-9 text-center !mb-0 pt-1"
      >
        Todas as condicoes devem ser verdadeiras (logica E).
      </p>
    </div>
  </div>
</template>
